import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/logo192.png';
import '../../styles/LeaveRequestPrint.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TYPE_LABELS = {
  annual: 'Annual leave',
  sick: 'Sick leave',
  maternity: 'Maternity leave',
  paternity: 'Paternity leave',
};

const showDate = (value) => {
  if (!value) return '—';
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match && text.length <= 10) {
    return `${match[3]} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${day} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
};

const inclusiveDays = (start, end) => {
  const startMatch = String(start || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const endMatch = String(end || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!startMatch || !endMatch) return '—';
  const startUtc = Date.UTC(Number(startMatch[1]), Number(startMatch[2]) - 1, Number(startMatch[3]));
  const endUtc = Date.UTC(Number(endMatch[1]), Number(endMatch[2]) - 1, Number(endMatch[3]));
  const days = Math.round((endUtc - startUtc) / 86400000) + 1;
  return days > 0 ? String(days) : '—';
};

const showDays = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  return String(Math.round(amount * 100) / 100);
};

const LeaveRequestPrint = ({ request, onClose }) => {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handlePrint = () => {
    document.body.classList.add('leave-request-printing');
    document.body.style.overflow = 'visible';
    const cleanup = () => {
      document.body.classList.remove('leave-request-printing');
      document.body.style.overflow = 'hidden';
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const employee = request.Employee || {};
  const manager = request.ManagerApprover;
  const hr = request.HrApprover;

  return createPortal(
    <div className="leave-print-overlay">
      <div className="leave-print-backdrop no-print" onClick={onClose} />
      <div className="leave-print-sheet" role="dialog" aria-modal="true" aria-labelledby="leave-print-title">
        <div className="leave-print-actions no-print">
          <button type="button" className="leave-print-button" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} /> Print
          </button>
          <button type="button" className="leave-print-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} /> Close
          </button>
        </div>

        <header className="leave-print-hospital">
          <img src={logo} alt="Shuman Hospital" className="leave-print-logo" />
          <div>
            <h2>Shuman Hospital</h2>
            <p>15-17 Pultney Street, Freetown</p>
            <p>Tel: (+232) 090 33 00 00</p>
          </div>
        </header>

        <h1 id="leave-print-title">Leave Request</h1>

        <table className="leave-print-table">
          <tbody>
            <tr>
              <th>Employee ID</th>
              <td>{employee.employee_id || request.employee_id}</td>
              <th>Employee name</th>
              <td>{employee.employee_name || '—'}</td>
            </tr>
            <tr>
              <th>Role</th>
              <td>{employee.role || '—'}</td>
              <th>Department</th>
              <td>{employee.department || '—'}</td>
            </tr>
            <tr>
              <th>Request date</th>
              <td colSpan="3">{showDate(request.createdAt)}</td>
            </tr>
          </tbody>
        </table>

        <div className={`leave-print-flag${request.prevented ? ' on' : ''}`}>
          <strong>Forfeited Leave Allowance</strong>
          <span>{request.prevented ? 'Yes' : 'No'}</span>
        </div>

        <h2>Leave asked for</h2>
        <table className="leave-print-table">
          <tbody>
            <tr>
              <th>Leave reason</th>
              <td colSpan="3">{TYPE_LABELS[request.leave_type] || request.leave_type || '—'}</td>
            </tr>
            <tr>
              <th>Start date</th>
              <td>{showDate(request.start_date)}</td>
              <th>End date</th>
              <td>{showDate(request.end_date)}</td>
            </tr>
            <tr>
              <th>Total days requested</th>
              <td>{inclusiveDays(request.start_date, request.end_date)}</td>
              <th>Total days of leave</th>
              <td>{showDays(request.working_days)}</td>
            </tr>
          </tbody>
        </table>

        <h2>Note</h2>
        <div className="leave-print-note">
          {request.certificate_note || 'None'}
        </div>

        <h2>Approval</h2>
        <div className="leave-print-approvals">
          <section>
            <h3>Manager</h3>
            <p><span>Name</span> {manager?.name || '—'}</p>
            <p><span>Title</span> {manager?.role || '—'}</p>
            <p><span>Date approved</span> {manager ? showDate(request.manager_approved_at) : '—'}</p>
            <div className="leave-print-sign">Signature</div>
          </section>
          <section>
            <h3>Human Resources</h3>
            <p><span>Name</span> {hr?.name || '—'}</p>
            <p><span>Title</span> {hr?.role || '—'}</p>
            <p><span>Date approved</span> {hr ? showDate(request.hr_approved_at) : '—'}</p>
            <div className="leave-print-sign">Signature</div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LeaveRequestPrint;
